import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useContext } from 'react';
import { ModalContext } from '../App';

const Home = () => {
  const features = [
    {
      title: "Simplified Legal Guidance",
      description: "Get quick, clear answers to your legal questions in plain language.",
      icon: "📚"
    },
    {
      title: "Personalized Advice",
      description: "Receive tailored recommendations based on your unique situation.",
      icon: "🎯"
    },
    {
      title: "Connect with Lawyers",
      description: "Easily find and connect with legal professionals for further assistance.",
      icon: "🤝"
    }
  ];

  const testimonials = [
    {
      user: "John Doe",
      feedback: "Civic Guru made understanding the legal process so much easier for me! I highly recommend it.",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=john"
    },
    {
      user: "Jane Smith",
      feedback: "A must-have for anyone needing quick legal advice. It's simple, fast, and accurate.",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=jane"
    }
  ];

  const { isModalOpen, setIsModalOpen } = useContext(ModalContext);

  const handleGetStarted = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="space-y-20">
      {/* Waitlist Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999]"
              onClick={() => setIsModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed left-[5%] md:left-[30%] top-[20vh] -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl p-8 shadow-2xl max-w-md w-[90%] md:w-full z-[10000]"
            >
              <div className="text-center">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">We're Almost Ready! 🚀</h3>
                  <p className="text-gray-600 mb-6">
                    Civic Guru is currently being perfected to provide you with the best legal assistance experience. 
                    Join our waitlist to be among the first to know when we launch!
                  </p>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="space-y-4"
                >
                  <a
                    href="https://forms.gle/a2x32pFTEsyWzZ6Q8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block w-full bg-primary-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-primary-700 transition-colors"
                  >
                    Join the Waitlist
                  </a>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="inline-block w-full text-gray-600 px-6 py-3 rounded-full font-medium hover:bg-gray-100 transition-colors"
                  >
                    Maybe Later
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-700 text-white py-20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzBoLTJWMGgydjMwem0tMiAwSDJ2MmgzMnYtMnptMCAydjI4aDJ2LTI4aC0yem0yIDBoMjh2LTJIMzZ2MnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        <div className="container mx-auto px-6 relative">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex-1 text-center lg:text-left"
            >
              <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
                Simplify Legal Jargon with{" "}
                <span className="bg-gradient-to-r from-accent-300 to-secondary-300 text-transparent bg-clip-text">
                  Civic Guru
                </span>
              </h1>
              <p className="text-xl md:text-2xl mb-12 text-gray-100 max-w-3xl">
                Your AI-powered Legal Assistant on WhatsApp. Accessible, reliable, and easy to use.
              </p>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/"
                  onClick={handleGetStarted}
                  className="inline-block bg-white text-primary-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1 cursor-pointer"
                >
                  Get Started Free
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ 
                duration: 0.8,
                delay: 0.2,
                type: "spring",
                stiffness: 100
              }}
              className="flex-1 flex justify-center items-center relative"
            >
              {/* Welcome Message Bubble */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="absolute left-0 md:left-[15%] top-10 md:top-20 bg-white text-primary-600 px-4 py-2 rounded-2xl shadow-lg z-10 transform -translate-x-1/4 md:-translate-x-0"
              >
                <p className="text-sm font-medium">Hi there!👋</p>
                <div className="absolute right-0 bottom-0 transform translate-x-1/2 translate-y-1/2 rotate-45 w-3 h-3 bg-white"></div>
              </motion.div>

              {/* Ready to Help Message */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.5, duration: 0.5 }}
                className="absolute right-0 md:right-[15%] top-32 bg-white text-primary-600 px-4 py-2 rounded-2xl shadow-lg z-10 transform translate-x-1/4 md:translate-x-0"
              >
                <p className="text-sm font-medium">I'm Civic Guru 🤖</p>
                <div className="absolute left-0 bottom-0 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-3 h-3 bg-white"></div>
              </motion.div>

              <div className="relative w-[300px] h-[300px] md:w-[400px] md:h-[400px]">
                <motion.img
                  src="/avatar.png"
                  alt="Civic Guru AI Assistant"
                  className="w-full h-full object-contain drop-shadow-2xl"
                  animate={{
                    scale: [1, 1.02, 1],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    repeatType: "reverse"
                  }}
                />
                {/* Glowing effect behind the avatar */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary-600/20 via-primary-400/10 to-transparent rounded-full filter blur-xl -z-10"></div>
              </div>

              {/* Legal Assistant Message */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2, duration: 0.5 }}
                className="absolute bottom-10 md:bottom-20 left-1/2 -translate-x-1/2 bg-white text-primary-600 px-4 py-2 rounded-2xl shadow-lg z-10"
              >
                <p className="text-sm font-medium">Your friendly legal assistant 📚⚖️</p>
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-3 h-3 bg-white"></div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="section-title">Why Choose Civic Guru?</h2>
          <motion.div 
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-12 mt-12"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={item}
                className="card text-center group hover:bg-primary-50"
              >
                <div className="text-4xl mb-4 transform group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-4 text-primary-600">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-secondary-50 to-primary-50">
        <div className="container mx-auto px-6">
          <h2 className="section-title">What Our Users Say</h2>
          <motion.div 
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-12"
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={item}
                className="card"
              >
                <div className="flex items-center mb-6">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.user}
                    className="w-16 h-16 rounded-full mr-4"
                  />
                  <div>
                    <h3 className="font-bold text-lg text-primary-600">{testimonial.user}</h3>
                    <div className="text-yellow-400">★★★★★</div>
                  </div>
                </div>
                <p className="text-gray-600 italic">{testimonial.feedback}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="container mx-auto px-6 text-center"
        >
          <h2 className="text-4xl font-bold mb-8">Ready to Simplify Legal Jargon?</h2>
          <p className="text-xl mb-12 text-gray-100 max-w-2xl mx-auto">
            Join thousands of users who are already benefiting from Civic Guru's AI-powered legal assistance.
          </p>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link
              to="/"
              onClick={handleGetStarted}
              className="inline-block bg-white text-primary-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Chat with Civic Guru Now
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default Home; 